'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode, TransitionEvent } from 'react';

import type { FaqEntry } from '@/data/shared/faq';

import SolutionFaqDisclosureIcon from './SolutionFaqDisclosureIcon';
import { faqSectionClasses } from './SolutionFaqSection.styles';
import type { SolutionSurfaceTone } from './solutionSurfaceTone';

/**
 * ### SolutionFaqItemProps
 *
 * One FAQ row of {@link SolutionFaqSection}: the copy to show and the surface
 * tone that decides its colors.
 */
export type SolutionFaqItemProps = {
  /**
   * ### entry
   *
   * Question and answer rendered verbatim; the question doubles as the row's
   * heading text.
   */
  entry: FaqEntry;

  /**
   * ### tone
   *
   * Surface tone of the enclosing section. Always passed down by the section
   * so a row never disagrees with the surface it sits on.
   */
  tone: SolutionSurfaceTone;
  followUp?: ReactNode;
  onExpansionChange?: (expanded: boolean) => void;
};

/**
 * ### SolutionFaqItem
 *
 * Renders one question as a heading-and-button disclosure over an answer
 * region. The answer is part of the server-rendered HTML whether or not the
 * row is expanded, so crawlers and the FAQPage structured data see the same
 * text a reader does.
 *
 * The collapsed answer is hidden with `hidden="until-found"`, which keeps it
 * out of the page and the accessibility tree while leaving it searchable:
 * a reader who presses Ctrl+F and types words from an answer has the row
 * opened and scrolled to for them. The browser announces that through
 * `beforematch`, which this row listens for.
 *
 * The row opens and closes over 200ms. Hidden content cannot animate, which
 * leaves nothing on screen to move, so the reveal is split in two: opening
 * shows the region first and starts the motion on the following frame, and
 * closing runs the motion first and hides the region once it lands.
 * `data-expanded` is what the row is styled from — it turns with the motion,
 * while the `hidden` attribute brackets it — so the answer and the plus/minus
 * indicator never disagree mid-flight.
 *
 * @param props Copy and surface tone of the row
 * @returns The rendered disclosure element
 * @see {@link SolutionFaqItemProps}
 */
export function SolutionFaqItem({
  entry,
  tone,
  followUp,
  onExpansionChange,
}: SolutionFaqItemProps) {
  const classes = faqSectionClasses(tone);
  const questionId = useId();
  const answerId = useId();
  const itemRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [isShown, setIsShown] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [opensInstantly, setOpensInstantly] = useState(false);

  /*
   * React writes `hidden` as a boolean attribute, so a string passed through
   * JSX would land as `hidden=""` and the answer would be hidden from
   * find-in-page along with everything else. The markup therefore ships the
   * boolean form — which hides the answer exactly the same way — and the
   * value is upgraded here once the row is on the client. Re-running on close
   * restores the value React has just overwritten.
   */
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || isShown) return;

    frame.setAttribute('hidden', 'until-found');
  }, [isShown]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    /*
     * The browser reveals the answer and scrolls to the match in the same
     * task this fires in, long before React can render, so the open state is
     * written to the DOM here as well as into state. Without it the reader
     * would be scrolled to a row whose answer is still clipped to no height.
     */
    const handleBeforeMatch = () => {
      const item = itemRef.current;
      if (item) {
        item.dataset.instant = '';
        item.dataset.expanded = '';
      }

      setOpensInstantly(true);
      setIsShown(true);
      setIsExpanded(true);
    };

    frame.addEventListener('beforematch', handleBeforeMatch);

    return () => frame.removeEventListener('beforematch', handleBeforeMatch);
  }, []);

  /* The motion is only suppressed for the frame that reveal lands in. */
  useEffect(() => {
    if (!opensInstantly) return;

    const frame = requestAnimationFrame(() => setOpensInstantly(false));

    return () => cancelAnimationFrame(frame);
  }, [opensInstantly]);

  /*
   * Enter and Space on the button arrive here as clicks too, so the keyboard
   * path needs no separate handler.
   */
  const handleQuestionClick = () => {
    onExpansionChange?.(!isExpanded);
    if (!isExpanded) {
      setIsShown(true);
      afterNextPaint(() => setIsExpanded(true));

      return;
    }

    setIsExpanded(false);

    // Without a transition to wait on, the closing frame never arrives.
    if (prefersReducedMotion()) {
      setIsShown(false);
    }
  };

  const handleFrameTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    const isFrameCollapsing =
      event.target === event.currentTarget &&
      event.propertyName === 'grid-template-rows' &&
      !isExpanded;

    if (isFrameCollapsing) {
      setIsShown(false);
    }
  };

  return (
    <div
      ref={itemRef}
      data-expanded={isExpanded ? '' : undefined}
      data-instant={opensInstantly ? '' : undefined}
      className={classes.item}
    >
      <h3>
        <button
          type="button"
          id={questionId}
          aria-controls={answerId}
          aria-expanded={isShown}
          onClick={handleQuestionClick}
          className={classes.question}
        >
          <span>{entry.question}</span>
          <SolutionFaqDisclosureIcon className={classes.icon} />
        </button>
      </h3>
      <div
        ref={frameRef}
        id={answerId}
        role="region"
        aria-labelledby={questionId}
        hidden={!isShown}
        className={classes.answerFrame}
        onTransitionEnd={handleFrameTransitionEnd}
      >
        <div className={classes.answerClip}>
          <p className={classes.answer}>{entry.answer}</p>
          {followUp}
        </div>
      </div>
    </div>
  );
}

/**
 * ### afterNextPaint
 *
 * Runs the callback once the answer has been painted at its closed height. The
 * open height has to be asked for after that paint, or both land in the same
 * frame and the browser jumps straight to the end with nothing to animate.
 *
 * A page that is not on screen paints no frames, so the wait would never end
 * there; the callback runs at once instead, which opens the row without motion
 * no one is there to see rather than leaving it half-open.
 *
 * @param callback What to run once the closed height has been painted
 */
function afterNextPaint(callback: () => void) {
  if (document.visibilityState !== 'visible') {
    callback();

    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
}

/**
 * ### prefersReducedMotion
 *
 * Whether the visitor asked for less motion. Read at the moment of the click
 * rather than once at mount, so a change to the system setting takes effect
 * without a reload.
 *
 * @returns True when the reduced-motion preference is set
 */
function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
